/**
 * Client-side recognition and copy for the adaptive-content-egress quota.
 *
 * Two server outcomes need special handling, and both are keyed on the wire
 * codes in `@shared/adaptiveEgressCodes` rather than on status alone:
 *
 *  - `429 adaptive_content_limit_exceeded` — the account spent its SGT-day
 *    allowance. Distinct from a burst-limit 429, which clears in a minute and
 *    must keep its own "slow down" copy.
 *  - `503 adaptive_content_budget_unavailable` — the ledger was unreachable so
 *    the server failed closed. Distinct from an ordinary 5xx, which is worth
 *    retrying.
 *
 * Neither may be retried: a retry cannot conjure allowance, and it only adds
 * load to a budget store that is already struggling. `apiUtils` enforces that;
 * this module supplies the predicates and the copy.
 */
import {
  ADAPTIVE_EGRESS_LIMIT_ERROR_CODE,
  ADAPTIVE_EGRESS_UNAVAILABLE_ERROR_CODE,
} from '@shared/adaptiveEgressCodes.js';
import { SINGAPORE_UTC_OFFSET_MS } from '@shared/sgtDay.js';

export {
  ADAPTIVE_EGRESS_LIMIT_ERROR_CODE,
  ADAPTIVE_EGRESS_UNAVAILABLE_ERROR_CODE,
};

/**
 * `fetchWithConfig` spreads the whole error body onto the thrown `Error`, so an
 * egress failure arrives as `{ error, limit, used, requestedUnits, resetAt }`
 * alongside `status`.
 */
interface EgressErrorShape {
  error?: unknown;
  resetAt?: unknown;
  status?: unknown;
}

const readCode = (value: unknown): string | null => {
  if (value == null || typeof value !== 'object') return null;
  const code = (value as EgressErrorShape).error;
  return typeof code === 'string' ? code : null;
};

/** The account has spent today's allowance of freshly generated content. */
export const isAdaptiveContentLimitError = (err: unknown): boolean =>
  readCode(err) === ADAPTIVE_EGRESS_LIMIT_ERROR_CODE;

/** The budget store was unreachable, so the server refused to generate. */
export const isAdaptiveBudgetUnavailableError = (err: unknown): boolean =>
  readCode(err) === ADAPTIVE_EGRESS_UNAVAILABLE_ERROR_CODE;

/** Either quota outcome — the set `apiUtils` must never retry. */
export const isAdaptiveEgressError = (err: unknown): boolean =>
  isAdaptiveContentLimitError(err) || isAdaptiveBudgetUnavailableError(err);

const SGT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * `resetAt` as a day in Singapore time — "30 Jul". Formatted by shifting into
 * SGT and reading UTC fields rather than via `toLocaleDateString`, so a student
 * whose device clock or timezone is wrong still sees the boundary the server
 * actually enforces. Returns `null` for a missing or unparseable timestamp.
 */
export const formatAdaptiveEgressResetDay = (
  resetAt: unknown
): string | null => {
  if (typeof resetAt !== 'string' || resetAt.length === 0) return null;
  const parsed = new Date(resetAt);
  const ms = parsed.getTime();
  if (Number.isNaN(ms)) return null;
  const shifted = new Date(ms + SINGAPORE_UTC_OFFSET_MS);
  return `${shifted.getUTCDate()} ${SGT_MONTHS[shifted.getUTCMonth()]}`;
};

/**
 * Student-facing copy for a spent daily allowance. Names the reset moment
 * because "try again later" is unactionable — the allowance is a calendar-day
 * one, so the honest answer is a date, not a duration.
 */
export const adaptiveContentLimitMessage = (err: unknown): string => {
  const resetDay = formatAdaptiveEgressResetDay(
    (err as EgressErrorShape | null)?.resetAt
  );
  return resetDay
    ? `That's today's limit for new questions. You can do more after midnight Singapore time (${resetDay}).`
    : "That's today's limit for new questions. You can do more after midnight (Singapore time).";
};

/**
 * The builders' copy for a spent allowance. Students keep the line above;
 * a teacher building homework or a worksheet hears what still works without
 * new content.
 */
export const TEACHER_CONTENT_LIMIT_MESSAGE =
  'You’ve reached today’s limit for previewing and adding questions. It resets at midnight. You can still copy past work as is, add your own questions, reorder, and save PDFs.';

/** The builders' copy for a spent allowance, or `null` for any other error. */
export const builderContentLimitMessage = (err: unknown): string | null =>
  isAdaptiveContentLimitError(err) ? TEACHER_CONTENT_LIMIT_MESSAGE : null;

/** Student-facing copy for a fail-closed budget store. */
export const adaptiveBudgetUnavailableMessage = (): string =>
  "Couldn't load a new question just now. Try again in a moment.";

/**
 * Copy for whichever egress outcome this is, or `null` when the error is not
 * one — callers fall through to their own messaging.
 */
export const adaptiveEgressMessage = (err: unknown): string | null => {
  if (isAdaptiveContentLimitError(err)) return adaptiveContentLimitMessage(err);
  if (isAdaptiveBudgetUnavailableError(err)) {
    return adaptiveBudgetUnavailableMessage();
  }
  return null;
};
